import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus } from "lucide-react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import TagIcon from "../../assets/tag-icon.svg";
import DeleteIcon from "../../assets/delete-modal-icon.svg";
import RedDeleteIcon from "../../assets/icons/RedDeleteIcon";
import { getTagDetail, updateTagSellingPrice, detachProductFromTag } from "../../services/tag-api";
import "../../style/productTags.css";

const TagDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isPartsURL, isAccessoriesURL, isGodownURL } = useSelector((state) => state.domain);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingProduct, setDeletingProduct] = useState(null);
  const [sellingPrice, setSellingPrice] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);

  useEffect(() => {
    const fetchTagDetail = async () => {
      try {
        setLoading(true);
        const response = await getTagDetail(id);
        setData(response);
      } catch (err) {
        setError(err.message || "Failed to fetch tag details");
      } finally {
        setLoading(false);
      }
    };

    fetchTagDetail();
  }, [id]);

  if (loading) {
    return (
      <div className="card rounded-4 p-4 border-0">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="card rounded-4 p-4 border-0">
        <h2>{error || "Tag not found"}</h2>
      </div>
    );
  }

  const tagIdentifier = data.results?.[0]?.unique_identifier_identifier || "Tag";

  const handleDetachProduct = async (productId) => {
    try {
      setLoading(true);
      await detachProductFromTag(productId);
      const updatedData = await getTagDetail(id);
      setData(updatedData);
      setShowDeleteModal(false);
      setDeletingProduct(null);
      toast.success("Product detached successfully!");
    } catch (error) {
      console.error("Error detaching product:", error);
      const errorMessage = error.response?.data?.errors?.detail || error.response?.data?.message || "Failed to detach product. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSellingPrice = async () => {
    try {
      setLoading(true);
      await updateTagSellingPrice(id, parseFloat(sellingPrice));
      const updatedData = await getTagDetail(id);
      setData(updatedData);
      setShowModal(false);
      setSellingPrice("");
      toast.success("Selling price updated successfully!");
    } catch (error) {
      console.error("Error updating selling price:", error);
      const errorMessage = error.response?.data?.errors?.detail || error.response?.data?.message || "Failed to update selling price. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const totalPages = Math.ceil((data?.count || 0) / itemsPerPage);

  return (
    <>
      <button
        onClick={() => navigate("/tags")}
        className="btn btn-link p-0 mb-3"
        style={{
          textDecoration: "none",
          color: isGodownURL ? "var(--primary-color-godown)" : "#1570EF",
          fontSize: "14px",
          fontWeight: "400",
          marginTop: "-5px",
        }}
      >
        <ArrowLeft size={16} style={{ marginRight: "8px" }} />
        Back to Tags
      </button>
      <div className="card rounded-4 p-4 border-0 tags-container tags-layout">
        <div className="tags-top">
          <div className="d-flex justify-content-between align-items-start mb-1">
            <div>
              <h2 className="mrph-txt size-22 weight-600 color-text-medium mb-1">
                Tag {tagIdentifier} Details
              </h2>
              <p className="mrph-txt size-14 weight-400 color-text-semi-regular">
                Check all products lists related to this {tagIdentifier} Tag
              </p>
            </div>
            <button
              onClick={() => setShowModal(true)}
              className="tags-update-btn"
            >
              <Plus size={16} style={{ marginRight: "4px" }} />
              Update Selling Price
            </button>
          </div>

          {/* summary cards */}
          <div className="row mb-4">
            <div className="col-md-3">
              <div
                className="card border rounded-3 p-3"
                style={{ height: "100px", width: "262px" }}
              >
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <p className="mrph-txt size-14 weight-400 color-text-semi-regular mb-2">
                      Total Products
                    </p>
                    <h3 className="mrph-txt size-24 weight-600 color-text-medium mb-0">
                      {data.summary?.total_products || 0}
                    </h3>
                  </div>
                  <img src={TagIcon} alt="Total Products" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="tags-table-container tags-table-wrapper">
          <table className="tags-table">
            <thead style={{ backgroundColor: "#F6F8FA" }}>
              <tr>
                <th>PRODUCT</th>
                <th>BUYING PRICE</th>
                <th>SELLING PRICE</th>
                <th>QTY.</th>
                <th style={{ textAlign: "center" }}>ACTION</th>
              </tr>
            </thead>
            <tbody className="tags-tbody">
              {data.results && data.results.length > 0 ? (
                data.results.map((product) => (
                  <tr
                    key={product.id}
                    className="mrph-txt size-14 color-text-regular"
                    style={{ height: "60px" }}
                  >
                    <td className="px-3">{product.display_name || product.name}</td>
                    <td className="px-3">€{product.latest_buying_price || 0}</td>
                    <td className="px-3">€{product.latest_selling_price || 0}</td>
                    <td className="px-3">{product.in_stock || 0}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="action-btn delete"
                          title="Delete"
                          onClick={() => {
                            setDeletingProduct(product);
                            setShowDeleteModal(true);
                          }}
                        >
                          <RedDeleteIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr className="mrph-txt size-14 color-text-regular" style={{ height: "60px" }}>
                  <td className="px-3" colSpan="5" style={{ textAlign: "center", color: "#9CA3AF" }}>
                    No products available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="tags-pagination-sticky">
            <div className="pagination-container">
              <div className="pagination-info">
                Page {currentPage} of {totalPages}
              </div>
              <div className="pagination-controls">
                <button
                  className={`${
                    isPartsURL
                      ? "pagination-btn-parts"
                      : isAccessoriesURL
                      ? "pagination-btn-acc"
                      : isGodownURL
                      ? "pagination-btn-godown"
                      : "pagination-btn"
                  } ${currentPage === 1 ? "disabled" : ""}`}
                  
                  onClick={handlePrevious}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <button
                  className={`${
                    isPartsURL
                      ? "pagination-btn-next-parts"
                      : isAccessoriesURL
                      ? "pagination-btn-next-acc"
                      : isGodownURL
                      ? "pagination-btn-next-godown"
                      : "pagination-btn-next"
                  } ${currentPage === totalPages ? "disabled" : ""}`}
                  onClick={handleNext}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="tags-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="tags-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="tags-modal-content">
              <div className="tags-modal-header">
                <h2 className="tags-modal-title">Update Selling Price</h2>
                <p className="tags-modal-subtitle">Edit and Update the selling price for this tags</p>
              </div>
              <div className="tags-modal-body">
                <div className="tags-form-group">
                  <label className="tags-form-label">Product *</label>
                  <input
                    type="text"
                    className="tags-form-input"
                    value={tagIdentifier}
                    disabled
                    style={{ backgroundColor: "#F9FAFB", color: "#6B7280" }}
                  />
                </div>
                <div className="tags-form-group">
                  <label className="tags-form-label">Selling Price*</label>
                  <input
                    type="number"
                    className="tags-form-input"
                    value={sellingPrice}
                    onChange={(e) => setSellingPrice(e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="tags-modal-footer">
                <button className="tags-btn-cancel" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button className="tags-btn-primary" onClick={handleUpdateSellingPrice}>
                  Update Selling Price
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && deletingProduct && (
        <div className="modal-overlay">
          <div className="delete-modal">
            <div className="modal-icon">
              <img src={DeleteIcon} alt="Delete" />
            </div>
            <h3 className="modal-title">Detach Product</h3>
            <p className="modal-message">
              Are you sure you want to detach <b>{deletingProduct.display_name || deletingProduct.name}</b> from this tag?
              <br />
              This action cannot be undone.
            </p>
            <div className="modal-actions">
              <button
                className="modal-cancel-btn"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletingProduct(null);
                }}
              >
                Cancel
              </button>
              <button
                className="modal-delete-btn"
                onClick={() => handleDetachProduct(deletingProduct.id)}
              >
                Detach
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TagDetail;
